import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import { ConfirmDialog, DataTable, EmptyState, ErrorState, FeedbackBanner, FieldGroup, FilterGrid, LoadingState, MvpCard, MvpTalentLayout, PaginationControls, StatusBadge } from '../components/MvpTalentUi';
import { formatDateTime } from '../utils/format';

const InstancesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [dialog, setDialog] = useState<{ id: number; instanceName: string; enabled: boolean } | null>(null);
  const filters = useMemo(() => ({ page: Number(searchParams.get('page') || 1), limit: 10, search: searchParams.get('search') || '' }), [searchParams]);
  const { data, loading, error, reload } = useAsyncData((signal) => platformCoreNetworkApi.listInstances(filters, { signal }), [filters.page, filters.search]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  return (
    <MvpTalentLayout title="Instance Management" subtitle="Global Core Network enablement and membership oversight by instance.">
      {feedback ? <FeedbackBanner tone={feedback.tone} message={feedback.message} onDismiss={() => setFeedback(null)} /> : null}
      <MvpCard title="Filters">
        <FilterGrid>
          <FieldGroup label="Search"><input value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} placeholder="Instance name or slug" /></FieldGroup>
        </FilterGrid>
      </MvpCard>
      {loading ? <LoadingState label="Loading instances..." /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <MvpCard title="Instances">
          {data.rows.length ? (
            <>
              <DataTable columns={['Instance', 'Slug', 'Enabled', 'Memberships', 'Requests', 'Updated', 'Actions']}>
                {data.rows.map((instance) => (
                  <tr key={instance.id}>
                    <td><Link to={`/command-center/mvp-talent/instances/${instance.id}`}>{instance.instanceName || `Instance ${instance.id}`}</Link></td>
                    <td>{instance.instanceUniqueId || '-'}</td>
                    <td><StatusBadge status={instance.mvpTalentEnabled ? 'active' : 'disabled'} /></td>
                    <td>{instance.membershipCount || 0}</td>
                    <td>{instance.requestCount || 0}</td>
                    <td>{formatDateTime(instance.updatedAt)}</td>
                    <td><button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialog({ id: instance.id, instanceName: instance.instanceName || `Instance ${instance.id}`, enabled: instance.mvpTalentEnabled })}>{instance.mvpTalentEnabled ? 'Disable' : 'Enable'}</button></td>
                  </tr>
                ))}
              </DataTable>
              <PaginationControls page={data.pagination.page} totalPages={data.pagination.pages} onPageChange={(nextPage) => updateFilter('page', String(nextPage))} />
            </>
          ) : <EmptyState title="No instances found" message="No instances matched the current filter." />}
        </MvpCard>
      ) : null}
      <ConfirmDialog
        open={Boolean(dialog)}
        title={dialog?.enabled ? 'Disable Core Network for instance' : 'Enable Core Network for instance'}
        description={dialog ? `${dialog.enabled ? 'Disabling' : 'Enabling'} ${dialog.instanceName} updates the existing backend mvpTalentEnabled setting.` : ''}
        confirmLabel={dialog?.enabled ? 'Disable instance' : 'Enable instance'}
        confirmTone={dialog?.enabled ? 'danger' : 'default'}
        onClose={() => setDialog(null)}
        onConfirm={async ({ reason }) => {
          if (!dialog) return;
          try {
            await platformCoreNetworkApi.updateInstanceSettings(dialog.id, { mvpTalentEnabled: !dialog.enabled, reason });
            setFeedback({ tone: 'success', message: `${dialog.instanceName} updated successfully.` });
            await reload();
          } catch (mutationError: any) {
            setFeedback({ tone: 'error', message: mutationError?.message || 'Unable to update the instance setting.' });
            throw mutationError;
          }
        }}
      />
    </MvpTalentLayout>
  );
};

export default InstancesPage;
